'use client';

import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useAppStore } from '@/store/app';
import { translate, LANGUAGES } from '@/lib/i18n';
import { api, setAuthTokens } from '@/lib/api';
import type { Language, Theme } from '@glintfans/shared';
import GlobalSearch from './GlobalSearch';
import MobileSearchButton from './MobileSearchButton';
import HealthPanel from './HealthPanel';

// R06: 'chart' (联动分析 /chart) added to the main nav so the fully-built
// linkage-analysis page is reachable from the UI. 'research' hosts the
// R01/R02/F01/E01/W01/H01 study pages (entry: /research/spot).
const navKeys = ['markets', 'discover', 'strategy', 'chart', 'signals', 'hyperliquid', 'news', 'research'] as const;

export default function Header() {
  const pathname = usePathname();
  const router = useRouter();
  const { language, theme, setLanguage, setTheme, user, setUser, setAuthState, authState } = useAppStore();
  const t = translate(language);
  const [menuOpen, setMenuOpen] = useState(false);
  const [healthOpen, setHealthOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // N01: 数据健康状态（顶部状态图标；仅登录用户，JWT 守卫）
  const { data: health } = useQuery({
    queryKey: ['health-summary'],
    queryFn: () => api.get<{ failedCount: number; staleCount: number; total: number }>('/health-center'),
    refetchInterval: 60_000,
    enabled: !!user,
  });

  // N01: 按当前页面映射数据源范围（默认展示当前页面相关指标）
  const healthScope = pathname.startsWith('/coin')
    ? 'market.tickers,market.derivatives,market.spotTickers'
    : pathname.startsWith('/news')
      ? 'news.feed'
      : pathname.startsWith('/calendar')
        ? 'calendar.events'
        : pathname.startsWith('/hyperliquid')
          ? 'hl.onchain'
          : pathname.startsWith('/research/studies') || pathname.startsWith('/signals')
            ? 'liquidation.events,market.derivatives'
            : pathname.startsWith('/discover')
              ? 'market.overview,market.tickers,liquidation.events'
              : undefined;

  // AUD01/R01: auth pages keep a light logo bar only; hide the full header.
  // The branch must live AFTER every Hook so the hook count stays stable when
  // the same Header instance navigates /login -> /markets (an early return
  // between hooks caused React #300/#310 "Rendered more hooks..." on route swap).
  const isAuthPage = pathname.startsWith('/login') || pathname.startsWith('/register');

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const { data: unread } = useQuery({
    queryKey: ['unread-count'],
    queryFn: () => api.get<{ count: number }>('/notifications/unread-count'),
    refetchInterval: 30_000,
    enabled: !!user,
  });

  if (isAuthPage) return null;

  const isActive = (key: string) => {
    if (key === 'markets') return pathname === '/' || pathname.startsWith('/markets') || pathname.startsWith('/coin');
    return pathname.startsWith(`/${key}`);
  };

  const logout = () => {
    setAuthTokens(null);
    setUser(null);
    setAuthState('guest');
    setMenuOpen(false);
    router.push('/markets');
  };

  const menuItem =
    'flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-gf-text transition-colors hover:bg-gf-surface2';

  return (
    <header className="sticky top-0 z-50 border-b border-gf-border bg-gf-bg/90 pt-[env(safe-area-inset-top)] backdrop-blur">
      <div className="mx-auto flex h-16 max-w-[1440px] items-center gap-2 px-2 sm:gap-3 sm:px-4 lg:px-6">
        <Link href="/markets" className="flex shrink-0 items-center">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={theme === 'light' ? '/logo-light.png' : '/logo-dark.png'}
            alt="AiLumix"
            className="h-10 w-auto rounded"
            style={{ maxWidth: 160 }}
          />
        </Link>

        <nav className="hidden items-center gap-0.5 xl:flex">
          {navKeys.map((key) => (
            <Link
              key={key}
              href={key === 'markets' ? '/markets' : `/${key}`}
              className={`rounded-md px-3 py-1.5 text-sm transition-colors ${
                isActive(key) ? 'text-gf-text' : 'text-gf-muted hover:text-gf-text'
              }`}
            >
              {t.nav[key]}
            </Link>
          ))}
        </nav>

        {/* Tablet compact nav menu (768–1199px): hamburger with all main + account entries */}
        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          className="hidden h-11 w-11 items-center justify-center rounded-lg border border-gf-border bg-gf-surface2 text-gf-text transition-colors hover:border-gf-accent/50 md:flex xl:hidden"
          aria-label={t.nav.more}
          aria-expanded={menuOpen}
        >
          {menuOpen ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M3 12h18M3 18h18" /></svg>
          )}
        </button>

        {menuOpen && (
          <>
            {/* Modal backdrop: inert page background while the tablet menu is open */}
            <div
              className="fixed inset-0 top-[calc(4rem+env(safe-area-inset-top))] z-40 bg-black/40 md:block xl:hidden"
              aria-hidden="true"
              onClick={() => setMenuOpen(false)}
            />
            <div
              className="fixed inset-x-0 top-[calc(4rem+env(safe-area-inset-top))] z-50 max-h-[calc(100vh-4rem)] overflow-y-auto border-b border-gf-border bg-gf-bg/95 px-4 pb-4 pt-2 backdrop-blur xl:hidden md:block"
              role="menu"
            >
            <div className="mx-auto grid max-w-[1440px] grid-cols-2 gap-1 sm:grid-cols-3">
              {navKeys.map((key) => (
                <Link
                  key={key}
                  href={key === 'markets' ? '/markets' : `/${key}`}
                  onClick={() => setMenuOpen(false)}
                  className={`flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm transition-colors ${
                    isActive(key) ? 'bg-gf-accent/10 text-gf-accent' : 'text-gf-text hover:bg-gf-surface2'
                  }`}
                  aria-current={isActive(key) ? 'page' : undefined}
                >
                  {t.nav[key]}
                </Link>
              ))}
              <Link href="/watchlist" onClick={() => setMenuOpen(false)} className={`flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm transition-colors ${pathname.startsWith('/watchlist') ? 'bg-gf-accent/10 text-gf-accent' : 'text-gf-text hover:bg-gf-surface2'}`}>
                {t.nav.watchlist}
              </Link>
              {user && (
                <>
                  <Link href="/notifications" onClick={() => setMenuOpen(false)} className={`flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm transition-colors ${pathname.startsWith('/notifications') ? 'bg-gf-accent/10 text-gf-accent' : 'text-gf-text hover:bg-gf-surface2'}`}>
                    {t.nav.notifications}
                  </Link>
                  <Link href="/alerts" onClick={() => setMenuOpen(false)} className={`flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm transition-colors ${pathname.startsWith('/alerts') ? 'bg-gf-accent/10 text-gf-accent' : 'text-gf-text hover:bg-gf-surface2'}`}>
                    {t.nav.alerts}
                  </Link>
                  <Link href="/portfolio" onClick={() => setMenuOpen(false)} className={`flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm transition-colors ${pathname.startsWith('/portfolio') ? 'bg-gf-accent/10 text-gf-accent' : 'text-gf-text hover:bg-gf-surface2'}`}>
                    {t.nav.portfolio}
                  </Link>
                  <Link href="/settings" onClick={() => setMenuOpen(false)} className={`flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm transition-colors ${pathname.startsWith('/settings') ? 'bg-gf-accent/10 text-gf-accent' : 'text-gf-text hover:bg-gf-surface2'}`}>
                    {t.nav.settings}
                  </Link>
                  <Link href="/workspace" onClick={() => setMenuOpen(false)} className={`flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm transition-colors ${pathname.startsWith('/workspace') ? 'bg-gf-accent/10 text-gf-accent' : 'text-gf-text hover:bg-gf-surface2'}`}>
                    {t.nav.workspace}
                  </Link>
                  <Link href="/profile" onClick={() => setMenuOpen(false)} className={`flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm transition-colors ${pathname.startsWith('/profile') ? 'bg-gf-accent/10 text-gf-accent' : 'text-gf-text hover:bg-gf-surface2'}`}>
                    {t.nav.profile}
                  </Link>
                </>
              )}
            </div>
            {!user && (
              <div className="mt-2 border-t border-gf-border pt-2">
                <Link href="/login" onClick={() => setMenuOpen(false)} className="btn-primary block w-full !h-10 text-sm">
                  {t.auth.login}
                </Link>
              </div>
            )}
            </div>
          </>
        )}

        <div className="ml-auto flex items-center gap-1.5 sm:gap-2">
          {/* N01: 数据状态图标（登录可见；绿=正常 黄=延迟 红=异常 灰=无记录） */}
          {user && (
            <button
              type="button"
              onClick={() => setHealthOpen((v) => !v)}
              className="relative flex h-9 w-9 items-center justify-center rounded-lg border border-gf-border bg-gf-surface2 text-gf-muted transition-colors hover:text-gf-text"
              title={t.health.dataStatus}
              aria-label={t.health.dataStatus}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 12a9 9 0 11-18 0 9 9 0 0118 0zM8.5 12h7M12 8.5v7" />
              </svg>
              {health && (
                <span
                  className={`absolute right-0 top-0 h-2.5 w-2.5 rounded-full ring-2 ring-gf-bg ${
                    health.failedCount > 0 ? 'bg-gf-negative' : health.staleCount > 0 ? 'bg-gf-warn' : health.total > 0 ? 'bg-gf-positive' : 'bg-gf-muted'
                  }`}
                />
              )}
            </button>
          )}

          {/* Watchlist shortcut (desktop) */}
          <Link
            href="/watchlist"
            className="hidden h-9 w-9 items-center justify-center rounded-lg border border-gf-border bg-gf-surface2 text-gf-muted transition-colors hover:text-gf-accent md:flex"
            title={t.nav.watchlist}
            aria-label={t.nav.watchlist}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill={pathname.startsWith('/watchlist') ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 3l2.4 4.9 5.4.8-3.9 3.8.9 5.4-4.8-2.5-4.8 2.5.9-5.4L4.2 8.7l5.4-.8z" />
            </svg>
          </Link>

          {/* Help center shortcut (desktop) */}
          <Link
            href="/help"
            className="hidden h-9 w-9 items-center justify-center rounded-lg border border-gf-border bg-gf-surface2 text-gf-muted transition-colors hover:text-gf-accent md:flex"
            title={t.nav.help}
            aria-label={t.nav.help}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="9" />
              <path d="M9.1 9a3 3 0 015.8 1c0 2-3 2.5-3 4M12 17h.01" />
            </svg>
          </Link>

          <div className="hidden items-center gap-1 lg:flex">
            <GlobalSearch />
          </div>
          <MobileSearchButton />

          {user ? (
            <>
              <Link
                href="/notifications"
                className="relative flex h-9 w-9 items-center justify-center rounded-lg border border-gf-border bg-gf-surface2 text-gf-muted transition-colors hover:text-gf-text"
                title={t.nav.notifications}
                aria-label={t.nav.notifications}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 8a6 6 0 00-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 01-3.4 0" />
                </svg>
                {unread && unread.count > 0 && (
                  <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-gf-negative px-1 text-[9px] font-bold text-white">
                    {unread.count > 99 ? '99+' : unread.count}
                  </span>
                )}
              </Link>

              {/* Avatar menu */}
              <div className="relative" ref={menuRef}>
                <button
                  type="button"
                  onClick={() => setMenuOpen((v) => !v)}
                  className="flex h-9 items-center gap-1.5 rounded-lg border border-gf-border bg-gf-surface2 px-1.5 text-gf-text transition-colors hover:border-gf-accent/50"
                  aria-label="Account menu"
                >
                  {user.avatarUrl ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img src={user.avatarUrl} alt="" className="h-7 w-7 rounded-full object-cover" />
                  ) : (
                    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-gf-accent/20 text-xs font-bold text-gf-accent">
                      {(user.displayName || user.email[0] || 'U').toUpperCase()}
                    </span>
                  )}
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gf-muted">
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </button>

                {menuOpen && (
                  <div className="absolute right-0 top-11 z-50 w-52 overflow-hidden rounded-xl border border-gf-border bg-gf-surface py-1.5 shadow-xl">
                    <div className="border-b border-gf-border px-3 py-2">
                      <div className="truncate text-sm font-semibold text-gf-text">{user.displayName || user.email}</div>
                      <div className="truncate text-[11px] text-gf-muted">{user.email}</div>
                    </div>
                    <Link href="/profile" className={menuItem} onClick={() => setMenuOpen(false)}>
                      <IconUser /> {t.nav.profile}
                    </Link>
                    <Link href="/workspace" className={menuItem} onClick={() => setMenuOpen(false)}>
                      <IconBook /> {t.nav.workspace}
                    </Link>
                    <Link href="/portfolio" className={menuItem} onClick={() => setMenuOpen(false)}>
                      <IconBook /> {t.nav.portfolio}
                    </Link>
                    <Link href="/alerts" className={menuItem} onClick={() => setMenuOpen(false)}>
                      <IconBell /> {t.nav.alerts}
                    </Link>
                    <Link href="/settings" className={menuItem} onClick={() => setMenuOpen(false)}>
                      <IconGear /> {t.nav.settings}
                    </Link>
                    <div className="my-1 border-t border-gf-border" />
                    <button type="button" onClick={logout} className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-gf-negative transition-colors hover:bg-gf-surface2">
                      <IconLogout /> {t.auth.logout}
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value as Language)}
                className="rounded-lg border border-gf-border bg-gf-surface2 px-1.5 py-1.5 text-[11px] text-gf-text outline-none sm:px-2 sm:text-xs"
                aria-label="Language"
              >
                {LANGUAGES.map((l) => (
                  <option key={l.value} value={l.value}>{l.label}</option>
                ))}
              </select>
              <button
                onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                className="rounded-lg border border-gf-border bg-gf-surface2 px-2 py-1.5 text-[11px] sm:text-xs"
                aria-label="Toggle theme"
              >
                {theme === 'dark' ? '☀' : '☾'}
              </button>
              <Link href="/login" className="btn-primary !h-9 !px-3 text-xs sm:!px-4 sm:text-sm">
                {authState === 'expired' ? t.auth.sessionExpired : t.auth.login}
              </Link>
            </>
          )}
        </div>
      </div>

      <HealthPanel open={healthOpen} onClose={() => setHealthOpen(false)} scope={healthScope} />
    </header>
  );
}

function IconUser() {
  return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 12a4 4 0 100-8 4 4 0 000 8zm-8 8a8 8 0 0116 0" /></svg>;
}
function IconBook() {
  return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h11a3 3 0 013 3v13H7a3 3 0 01-3-3zM4 4v16" /></svg>;
}
function IconBell() {
  return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8a6 6 0 00-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 01-3.4 0" /></svg>;
}
function IconGear() {
  return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 15a3 3 0 100-6 3 3 0 000 6zm7.4-3a7.4 7.4 0 00-.1-1.2l2-1.5-2-3.5-2.4 1a7.6 7.6 0 00-2-1.2L14.5 3h-4l-.4 2.6a7.6 7.6 0 00-2 1.2l-2.4-1-2 3.5 2 1.5a7.4 7.4 0 000 2.4l-2 1.5 2 3.5 2.4-1a7.6 7.6 0 002 1.2l.4 2.6h4l.4-2.6a7.6 7.6 0 002-1.2l2.4 1 2-3.5-2-1.5c.06-.4.1-.8.1-1.2z" /></svg>;
}
function IconLogout() {
  return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" /></svg>;
}